import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, type DropdownProps } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getDisplayToday } from "@/lib/displayDateTime";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * On-brand replacement for react-day-picker's NATIVE month/year `<select>`
 * (whose open list is painted by the OS — unstyleable, off-brand). Renders the
 * options through the shadcn `Select` so the dropdown matches the rest of the
 * app. rdp's `onChange` expects a change event, so we synthesise one.
 *
 * NOTE: the Select list portals to <body>, outside the calendar's Popover. The
 * `data-calendar-dropdown` marker lets the DatePicker's Popover keep itself open
 * when the interaction is with this dropdown (see date-picker.tsx).
 */
function CalendarDropdown({ value, onChange, children, "aria-label": ariaLabel }: DropdownProps) {
  const options = React.Children.toArray(children) as React.ReactElement<
    React.OptionHTMLAttributes<HTMLOptionElement>
  >[];
  const selected = options.find((o) => String(o.props.value) === String(value));

  return (
    <Select
      value={value != null ? String(value) : undefined}
      onValueChange={(next) =>
        onChange?.({ target: { value: next } } as React.ChangeEvent<HTMLSelectElement>)
      }
    >
      <SelectTrigger
        aria-label={ariaLabel}
        className="h-7 w-fit gap-1 border-none bg-transparent px-2 py-0 text-sm font-medium shadow-none focus:ring-1 focus:ring-ring data-[state=open]:bg-accent hover:bg-accent"
      >
        <SelectValue>{selected?.props?.children}</SelectValue>
      </SelectTrigger>
      <SelectContent
        data-calendar-dropdown
        position="popper"
        className="z-[300] max-h-72"
      >
        {options.map((option, i) => (
          <SelectItem key={`${option.props.value}-${i}`} value={String(option.props.value)}>
            {option.props.children}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Calendar({ className, classNames, showOutsideDays = true, today, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      /* "Today" (highlight + default month) is the New York day, wherever the viewer is. */
      today={today ?? getDisplayToday()}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100"),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
        Dropdown: CalendarDropdown,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
